// api.js — Reva Jewellers API Service

const BASE_URL = "https://api-dev.revajewellers.ashtamtechnologies.com/api";
export const BASE_ORIGIN = "https://api-dev.revajewellers.ashtamtechnologies.com";
export const toAbsUrl = (path) =>
  !path ? null : path.startsWith("http") ? path : `${BASE_ORIGIN}/${path.replace(/^\//, "")}`;

/* ─── tiny request helper ─── */
/* ─── tiny request helper ─── */
async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;

  const token = localStorage.getItem("authToken");


  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  let data = null;

  try {
    data = await res.json();
  } catch {
    // no JSON body
  }



  if (!res.ok) {

    // Auto logout on 401
    if (res.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");

      window.location.href = "/";
      return;
    }

    const err = new Error(
      data?.message ||
      data?.Message ||
      data?.error ||
      data?.title ||
      (data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : null) ||
      `Request failed (${res.status})`
    );

    err.response = { status: res.status, data };

    throw err;
  }

  return data;
}

/* ─── decode JWT (no library needed) ─── */
function decodeJWT(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return null;
  }
}

/* ════════════════════════════════════════════
   AUTH SERVICE
════════════════════════════════════════════ */
export const apiService = {

  /* ── Login ── */
loginUser: async ({ email, password }) => {
    const res = await fetch(`${BASE_URL}/Login/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (data?.forcePasswordChange === true) {
      return {
        forcePasswordChange: true,
        userId: data.userId ?? data.user?.id ?? null,
        email,
        oldPassword: password,
      };
    }

    const token = data?.token ?? data?.data?.token ?? data?.accessToken;
    if (!token) throw new Error("No token received from server.");

    const payload = decodeJWT(token);
    if (payload?.exp && Date.now() / 1000 > payload.exp) {
      throw new Error("Session expired. Please try again.");
    }

    localStorage.setItem("authToken", token.replace(/^Bearer\s+/i, ""));
    if (data?.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
   const userData = data?.user || data;

localStorage.setItem("user", JSON.stringify(userData));

    const roleId = data?.roleId ?? data?.user?.roleId ?? payload?.roleId;
    return { ...data, roleId, token };
  },

  /* ── Forced / first-time password change ──
     Exact payload the backend expects:
     { email, oldPassword, newPassword }
  ── */
  changePassword: async ({ email, oldPassword, newPassword }) => {
    return request("/Login/change-password", {
      method: "POST",
      body: JSON.stringify({ email, oldPassword, newPassword }),
    });
  },

  /* ── Forgot password ── */
  forgotPassword: async (email) => {
    return request("/Login/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  /* ── Logout ── */
  logout: () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  },

  /* ── Get stored token ── */
  getToken: () => localStorage.getItem("authToken"),

  /* ── Check if logged in ── */
  isAuthenticated: () => {
    const token = localStorage.getItem("authToken");
    if (!token) return false;
    const payload = decodeJWT(token);
    if (!payload?.exp) return true;
    return Date.now() / 1000 < payload.exp;
  },
};

/* ════════════════════════════════════════════
   ATTRIBUTE MASTER API
════════════════════════════════════════════ */
export const attributeApi = {

  /* ── GET all attributes ──
     GET /api/AttributeMaster
  ── */
  getAll: () =>
    request("/AttributeMaster", { method: "GET" }),

  /* ── GET single attribute by ID ──
     GET /api/AttributeMaster/{id}
  ── */
  getById: (id) =>
    request(`/AttributeMaster/${id}`, { method: "GET" }),

  /* ── POST create attribute ──
     POST /api/AttributeMaster
     Body: { attributeID: 0, attributeName, attributeType, isActive }
  ── */
  create: ({ attributeName, attributeType, isActive = true }) =>
    request("/AttributeMaster", {
      method: "POST",
      body: JSON.stringify({
        attributeID:   0,
        attributeName: attributeName.trim(),
        attributeType: attributeType.trim(),
        isActive,
      }),
    }),

  /* ── PUT update attribute ──
     PUT /api/AttributeMaster
     Body: { attributeID, attributeName, attributeType, isActive }
  ── */
  update: ({ attributeID, attributeName, attributeType, isActive }) =>
    request("/AttributeMaster", {
      method: "PUT",
      body: JSON.stringify({
        attributeID,
        attributeName: attributeName.trim(),
        attributeType: attributeType.trim(),
        isActive,
      }),
    }),

  /* ── DELETE attribute by ID ──
     DELETE /api/AttributeMaster/{id}
  ── */
  remove: (id) =>
    request(`/AttributeMaster/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════════════
   PRODUCT CATEGORY API
   ─────────────────────────────────────────────
   Paste this block into your api.js file.
   All requests automatically use BASE_URL,
   so switching dev → UAT → prod is one-line:
     const BASE_URL = "https://your-new-host/api";
════════════════════════════════════════════ */

export const productCategoryApi = {

  /* ── GET all categories ──
     GET /api/ProductCategory
     Returns: array of category objects
  ── */
  getAll: () =>
    request("/ProductCategory", { method: "GET" }),

  /* ── GET single category by ID ──
     GET /api/ProductCategory/{id}
  ── */
  getById: (id) =>
    request(`/ProductCategory/${id}`, { method: "GET" }),

  /* ── POST create category ──
     POST /api/ProductCategory
     Body: {
       categoryID:      0,          // always 0 for new
       categoryName:    string,     // required
       parentCategoryID: number,    // 0 for top-level
       taxConfigID:     number,     // 0 if not applicable
       codeHSN:         string,     // HSN code, can be ""
     }
  ── */
  create: ({ categoryName, parentCategoryID = 0, taxConfigID = 0, codeHSN = "" }) =>
    request("/ProductCategory", {
      method: "POST",
      body: JSON.stringify({
        categoryID:       0,
        categoryName:     categoryName.trim(),
        parentCategoryID: Number(parentCategoryID) || 0,
        taxConfigID:      Number(taxConfigID)      || 0,
        codeHSN:          codeHSN.trim(),
      }),
    }),

  /* ── PUT update category ──
     PUT /api/ProductCategory
     Body: full category object with categoryID
  ── */
  update: ({ categoryID, categoryName, parentCategoryID = 0, taxConfigID = 0, codeHSN = "" }) =>
    request("/ProductCategory", {
      method: "PUT",
      body: JSON.stringify({
        categoryID,
        categoryName:     categoryName.trim(),
        parentCategoryID: Number(parentCategoryID) || 0,
        taxConfigID:      Number(taxConfigID)      || 0,
        codeHSN:          codeHSN.trim(),
      }),
    }),

  /* ── DELETE category by ID ──
     DELETE /api/ProductCategory/{id}
  ── */
  remove: (id) =>
    request(`/ProductCategory/${id}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   TAX CONFIG API
   ─────────────────────────────────────────────
   Paste this block into your api.js file,
   right after the productCategoryApi block.
   All requests use the shared BASE_URL and
   request() helper already defined in api.js,
   so switching dev → UAT → prod is one line:
     const BASE_URL = "https://your-new-host/api";
════════════════════════════════════════════ */

export const taxConfigApi = {

  /* ── GET all tax configs ──
     GET /api/TaxConfig
     Returns: flat array — all records including
     historical entries (same taxName, different effdt).
     The UI groups them client-side by taxName to build
     an effective-date history timeline.
  ── */
  getAll: () =>
    request("/TaxConfig", { method: "GET" }),

  /* ── GET single tax config by ID ──
     GET /api/TaxConfig/{id}
  ── */
  getById: (id) =>
    request(`/TaxConfig/${id}`, { method: "GET" }),

  /* ── POST create tax config ──
     POST /api/TaxConfig
     Body: {
       taxConfigID:   0,           // always 0 for new
       effdt:         "YYYY-MM-DD",// effective date (ISO date string)
       taxName:       string,      // required
       taxPercentage: number,      // 0–100
       isActive:      boolean,
     }
     Note: Changing effdt on an existing taxName
     creates a new history entry in the backend.
     The UI reflects this automatically on next fetch.
  ── */
  create: ({ taxName, taxPercentage, effdt, isActive = true }) =>
    request("/TaxConfig", {
      method: "POST",
      body: JSON.stringify({
        taxConfigID:   0,
        effdt,
        taxName:       taxName.trim(),
        taxPercentage: Number(taxPercentage),
        isActive,
      }),
    }),

  /* ── PUT update tax config ──
     PUT /api/TaxConfig
     Body: full object with taxConfigID.
     If effdt is changed relative to the current record,
     this will create a new history entry server-side.
  ── */
  update: ({ taxConfigID, taxName, taxPercentage, effdt, isActive }) =>
    request("/TaxConfig", {
      method: "PUT",
      body: JSON.stringify({
        taxConfigID,
        effdt,
        taxName:       taxName.trim(),
        taxPercentage: Number(taxPercentage),
        isActive,
      }),
    }),

  /* ── DELETE tax config by ID ──
     DELETE /api/TaxConfig/{id}
     Deletes a single record (one history entry).
     To remove all history for a taxName, call
     remove() for each entry's taxConfigID.
  ── */
  remove: (id) =>
    request(`/TaxConfig/${id}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   CATEGORY ATTRIBUTE MAP API
   ─────────────────────────────────────────────
   Add this block to api.js, after the
   taxConfigApi block and before the
   `export default apiService;` line.
════════════════════════════════════════════ */

export const categoryAttributeMapApi = {

  /* ── GET all mappings ──
     GET /api/CategoryAttributeMap
     Returns: array of map objects
     { mapID, categoryID, attributeID, isRequired }
  ── */
  getAll: () =>
    request("/CategoryAttributeMap", { method: "GET" }),

  /* ── GET single mapping by ID ──
     GET /api/CategoryAttributeMap/{id}
  ── */
  getById: (id) =>
    request(`/CategoryAttributeMap/${id}`, { method: "GET" }),

  /* ── POST create mapping ──
     POST /api/CategoryAttributeMap
     Body: {
       mapID:       0,       // always 0 for new
       categoryID:  number,  // required
       attributeID: number,  // required
       isRequired:  boolean,
     }
  ── */
  create: ({ categoryID, attributeID, isRequired = false }) =>
    request("/CategoryAttributeMap", {
      method: "POST",
      body: JSON.stringify({
        mapID:       0,
        categoryID:  Number(categoryID),
        attributeID: Number(attributeID),
        isRequired,
      }),
    }),

  /* ── PUT update mapping ──
     PUT /api/CategoryAttributeMap
     Body: full object with mapID
  ── */
  update: ({ mapID, categoryID, attributeID, isRequired }) =>
    request("/CategoryAttributeMap", {
      method: "PUT",
      body: JSON.stringify({
        mapID,
        categoryID:  Number(categoryID),
        attributeID: Number(attributeID),
        isRequired,
      }),
    }),

  /* ── DELETE mapping by mapID ──
     DELETE /api/CategoryAttributeMap/{mapID}
  ── */
  remove: (mapID) =>
    request(`/CategoryAttributeMap/${mapID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   VENDOR API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   categoryAttributeMapApi block (or after
   taxConfigApi) and before:
     export default apiService;
════════════════════════════════════════════ */

export const vendorApi = {

  /* ── GET all vendors ──
     GET /api/Vendor
     Returns: array of vendor objects
     { vendorID, vendorName, isInHouse,
       phoneNumber, contactNumber, address }
  ── */
  getAll: () =>
    request("/Vendor", { method: "GET" }),

  /* ── GET single vendor by ID ──
     GET /api/Vendor/{id}
  ── */
  getById: (id) =>
    request(`/Vendor/${id}`, { method: "GET" }),

  /* ── POST create vendor ──
     POST /api/Vendor
     Body: {
       vendorID:      0,       // always 0 for new
       vendorName:    string,  // required
       isInHouse:     boolean,
       phoneNumber:   string,
       contactNumber: string,
       address:       string,
     }
  ── */
  create: ({ vendorName, isInHouse = false, phoneNumber = "", contactNumber = "", address = "" }) =>
    request("/Vendor", {
      method: "POST",
      body: JSON.stringify({
        vendorID:      0,
        vendorName:    vendorName.trim(),
        isInHouse,
        phoneNumber:   phoneNumber.trim(),
        contactNumber: contactNumber.trim(),
        address:       address.trim(),
      }),
    }),

  /* ── PUT update vendor ──
     PUT /api/Vendor
     Body: full object with vendorID
  ── */
  update: ({ vendorID, vendorName, isInHouse, phoneNumber = "", contactNumber = "", address = "" }) =>
    request("/Vendor", {
      method: "PUT",
      body: JSON.stringify({
        vendorID,
        vendorName:    vendorName.trim(),
        isInHouse,
        phoneNumber:   phoneNumber.trim(),
        contactNumber: contactNumber.trim(),
        address:       address.trim(),
      }),
    }),

  /* ── DELETE vendor by ID ──
     DELETE /api/Vendor/{vendorID}
  ── */
  remove: (vendorID) =>
    request(`/Vendor/${vendorID}`, { method: "DELETE" }),
};

/* ════════════════════════════════════════════
   PRODUCT API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   vendorApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const productApi = {

  /* ── GET all products ──
     GET /api/Product
     Returns: array of product objects
     {
       productID, productCode, productName,
       categoryID, vendorID, pricingType,
       purityStandard, barcodeType, isActive,
       codeHSNOverride, codeHSN,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/Product", { method: "GET" }),

  /* ── GET single product by ID ──
     GET /api/Product/{id}
  ── */
  getById: (id) =>
    request(`/Product/${id}`, { method: "GET" }),

  /* ── POST create product ──
     POST /api/Product
     Body: {
       productID:       0,          // always 0 for new
       productCode:     string,     // required
       productName:     string,     // required
       categoryID:      number,     // required
       vendorID:        number,     // required
       pricingType:     string,     // e.g. "Fixed", "Per Gram"
       purityStandard:  string,     // e.g. "22K", "925 Silver"
       barcodeType:     string,     // e.g. "QR Code", "Code128"
       isActive:        boolean,
       codeHSNOverride: boolean,    // true = use codeHSN below
       codeHSN:         string,     // HSN override value
       lastUPDTTM:      string,     // ISO datetime — auto-set below
       lastUPDTID:      number,     // 0 default
     }
  ── */
  create: ({
    productCode,
    productName,
    categoryID,
    vendorID,
    pricingType     = "",
    purityStandard  = "",
    barcodeType     = "",
    isActive        = true,
    codeHSNOverride = false,
    codeHSN         = "",
  }) =>
    request("/Product", {
      method: "POST",
      body: JSON.stringify({
        productID:       0,
        productCode:     productCode.trim(),
        productName:     productName.trim(),
        categoryID:      Number(categoryID),
        vendorID:        Number(vendorID),
        pricingType:     pricingType.trim(),
        purityStandard:  purityStandard.trim(),
        barcodeType:     barcodeType.trim(),
        isActive,
        codeHSNOverride,
        codeHSN:         codeHSN.trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID:      0,
      }),
    }),

  /* ── PUT update product ──
     PUT /api/Product
     Body: full product object with productID
  ── */
  update: ({
    productID,
    productCode,
    productName,
    categoryID,
    vendorID,
    pricingType     = "",
    purityStandard  = "",
    barcodeType     = "",
    isActive        = true,
    codeHSNOverride = false,
    codeHSN         = "",
    lastUPDTID      = 0,
  }) =>
    request("/Product", {
      method: "PUT",
      body: JSON.stringify({
        productID,
        productCode:     productCode.trim(),
        productName:     productName.trim(),
        categoryID:      Number(categoryID),
        vendorID:        Number(vendorID),
        pricingType:     pricingType.trim(),
        purityStandard:  purityStandard.trim(),
        barcodeType:     barcodeType.trim(),
        isActive,
        codeHSNOverride,
        codeHSN:         codeHSN.trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE product by ID ──
     DELETE /api/Product/{productID}
  ── */
  remove: (productID) =>
    request(`/Product/${productID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   PRODUCT ATTRIBUTE DETAIL API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   productApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const productAttributeDtlApi = {

  /* ── GET all product attribute details ──
     GET /api/ProductAttributeDtl
     Returns: array of records
     {
       productAttributeID,
       productID,
       attributeID,
       attributeValueString,
       attributeValueDecimal,
       attributeValueInteger,
     }
  ── */
  getAll: () =>
    request("/ProductAttributeDtl", { method: "GET" }),

  /* ── GET single record by ID ──
     GET /api/ProductAttributeDtl/{id}
  ── */
  getById: (id) =>
    request(`/ProductAttributeDtl/${id}`, { method: "GET" }),

  /* ── POST create product attribute ──
     POST /api/ProductAttributeDtl
     Body: {
       productAttributeID:   0,        // always 0 for new
       productID:            number,   // required
       attributeID:          number,   // required
       attributeValueString: string,   // populate only the relevant field
       attributeValueDecimal: number,  // for decimal/float type attributes
       attributeValueInteger: number,  // for integer type attributes
     }
     Note: Only one of the three value fields should be
     populated at a time, based on the attribute's type.
     The other two should be sent as "" / 0 respectively.
  ── */
  create: ({
    productID,
    attributeID,
    attributeValueString  = "",
    attributeValueDecimal = 0,
    attributeValueInteger = 0,
  }) =>
    request("/ProductAttributeDtl", {
      method: "POST",
      body: JSON.stringify({
        productAttributeID:   0,
        productID:            Number(productID),
        attributeID:          Number(attributeID),
        attributeValueString: String(attributeValueString ?? "").trim(),
        attributeValueDecimal: attributeValueDecimal !== "" && attributeValueDecimal !== null
          ? Number(attributeValueDecimal) : 0,
        attributeValueInteger: attributeValueInteger !== "" && attributeValueInteger !== null
          ? Number(attributeValueInteger) : 0,
      }),
    }),

  /* ── PUT update product attribute ──
     PUT /api/ProductAttributeDtl
     Body: full object with productAttributeID
  ── */
  update: ({
    productAttributeID,
    productID,
    attributeID,
    attributeValueString  = "",
    attributeValueDecimal = 0,
    attributeValueInteger = 0,
  }) =>
    request("/ProductAttributeDtl", {
      method: "PUT",
      body: JSON.stringify({
        productAttributeID,
        productID:            Number(productID),
        attributeID:          Number(attributeID),
        attributeValueString: String(attributeValueString ?? "").trim(),
        attributeValueDecimal: attributeValueDecimal !== "" && attributeValueDecimal !== null
          ? Number(attributeValueDecimal) : 0,
        attributeValueInteger: attributeValueInteger !== "" && attributeValueInteger !== null
          ? Number(attributeValueInteger) : 0,
      }),
    }),

  /* ── DELETE product attribute by ID ──
     DELETE /api/ProductAttributeDtl/{productAttributeID}
  ── */
  remove: (productAttributeID) =>
    request(`/ProductAttributeDtl/${productAttributeID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   INVENTORY SUMMARY API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   productAttributeDtlApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const inventorySummaryApi = {

  /* ── GET all inventory entries ──
     GET /api/InventorySummary
     Returns: array of stock objects
     {
       stockID, productID, quantity,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/InventorySummary", { method: "GET" }),

  /* ── GET single inventory entry by stockID ──
     GET /api/InventorySummary/{stockID}
  ── */
  getById: (stockID) =>
    request(`/InventorySummary/${stockID}`, { method: "GET" }),

  /* ── POST create inventory entry ──
     POST /api/InventorySummary
     Body: {
       stockID:    0,          // always 0 for new
       productID:  number,     // required — links to Product
       quantity:   number,     // required — current stock count
       lastUPDTTM: string,     // ISO datetime — auto-set below
       lastUPDTID: number,     // 0 default
     }
  ── */
  create: ({ productID, quantity, lastUPDTID = 0 }) =>
    request("/InventorySummary", {
      method: "POST",
      body: JSON.stringify({
        stockID:    0,
        productID:  Number(productID),
        quantity:   Number(quantity),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update inventory entry ──
     PUT /api/InventorySummary
     Body: full object with stockID
  ── */
  update: ({ stockID, productID, quantity, lastUPDTID = 0 }) =>
    request("/InventorySummary", {
      method: "PUT",
      body: JSON.stringify({
        stockID,
        productID:  Number(productID),
        quantity:   Number(quantity),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE inventory entry by stockID ──
     DELETE /api/InventorySummary/{stockID}
  ── */
  remove: (stockID) =>
    request(`/InventorySummary/${stockID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   INVENTORY ITEM DTL API  — FULL CRUD
   ─────────────────────────────────────────────
   Replace the existing inventoryItemDtlApi
   block in api.js (the one that only had
   getAll and getById) with this full version.
════════════════════════════════════════════ */

export const inventoryItemDtlApi = {

  /* ── GET all inventory item details ──
     GET /api/InventoryItemDtl
  ── */
  getAll: () =>
    request("/InventoryItemDtl", { method: "GET" }),

  /* ── GET single item by ID ──
     GET /api/InventoryItemDtl/{id}
  ── */
  getById: (id) =>
    request(`/InventoryItemDtl/${id}`, { method: "GET" }),

  /* ── POST create inventory item ──
     POST /api/InventoryItemDtl
     Body: {
       inventoryItemID: 0,          // always 0 for new
       stockID:         number,     // 0 if not linked
       productID:       number,     // required
       receiptLineID:   number,     // 0 if not applicable
       grossWeight:     number,     // in grams
       lessWeight:      number,     // in grams
       netWeight:       number,     // grossWeight - lessWeight
       quantity:        number,     // required
       barcodeID:       number,     // 0 if not assigned
       lastUPDTTM:      string,     // ISO datetime — auto-set
       lastUPDTID:      number,     // 0 default
     }
  ── */
  create: ({
    stockID       = 0,
    productID,
    receiptLineID = 0,
    grossWeight   = 0,
    lessWeight    = 0,
    netWeight     = 0,
    quantity,
    barcodeID     = 0,
    lastUPDTID    = 0,
  }) =>
    request("/InventoryItemDtl", {
      method: "POST",
      body: JSON.stringify({
        inventoryItemID: 0,
        stockID:         Number(stockID)       || 0,
        productID:       Number(productID),
        receiptLineID:   Number(receiptLineID) || 0,
        grossWeight:     parseFloat(grossWeight)   || 0,
        lessWeight:      parseFloat(lessWeight)    || 0,
        netWeight:       parseFloat(netWeight)     || 0,
        quantity:        Number(quantity),
        barcodeID:       Number(barcodeID)     || 0,
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update inventory item ──
     PUT /api/InventoryItemDtl
     Body: full object with inventoryItemID
  ── */
  update: ({
    inventoryItemID,
    stockID       = 0,
    productID,
    receiptLineID = 0,
    grossWeight   = 0,
    lessWeight    = 0,
    netWeight     = 0,
    quantity,
    barcodeID     = 0,
    lastUPDTID    = 0,
  }) =>
    request("/InventoryItemDtl", {
      method: "PUT",
      body: JSON.stringify({
        inventoryItemID,
        stockID:         Number(stockID)       || 0,
        productID:       Number(productID),
        receiptLineID:   Number(receiptLineID) || 0,
        grossWeight:     parseFloat(grossWeight)   || 0,
        lessWeight:      parseFloat(lessWeight)    || 0,
        netWeight:       parseFloat(netWeight)     || 0,
        quantity:        Number(quantity),
        barcodeID:       Number(barcodeID)     || 0,
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE inventory item by ID ──
     DELETE /api/InventoryItemDtl/{inventoryItemID}
  ── */
  remove: (inventoryItemID) =>
    request(`/InventoryItemDtl/${inventoryItemID}`, { method: "DELETE" }),
};




/* ════════════════════════════════════════════
   INVENTORY TRANSACTION API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   inventoryItemDtlApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const inventoryTransactionApi = {

  /* ── GET all transactions ──
     GET /api/InventoryTransactionDtl
     Returns: array of transaction objects
     {
       transactionID, inventoryItemID, stockID,
       productID, transactionType, quantity,
       comments, lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/InventoryTransactionDtl", { method: "GET" }),

  /* ── GET single transaction by ID ──
     GET /api/InventoryTransactionDtl/{id}
  ── */
  getById: (id) =>
    request(`/InventoryTransactionDtl/${id}`, { method: "GET" }),

  /* ── POST create transaction ──
     POST /api/InventoryTransactionDtl
     Body: {
       transactionID:   0,        // always 0 for new
       inventoryItemID: number,   // from InventoryItem
       stockID:         number,   // from InventorySummary
       productID:       number,   // required — from Product
       transactionType: string,   // "Stock In" | "Stock Out" | "Adjustment" | "Damage"
       quantity:        number,   // required
       comments:        string,   // optional notes
       lastUPDTTM:      string,   // ISO datetime — auto-set below
       lastUPDTID:      number,   // 0 default
     }
  ── */
  create: ({
    inventoryItemID = 0,
    stockID         = 0,
    productID,
    transactionType,
    quantity,
    comments        = "",
    lastUPDTID      = 0,
  }) =>
    request("/InventoryTransactionDtl", {
      method: "POST",
      body: JSON.stringify({
        transactionID:   0,
        inventoryItemID: Number(inventoryItemID) || 0,
        stockID:         Number(stockID)         || 0,
        productID:       Number(productID),
        transactionType,
        quantity:        Number(quantity),
        comments:        String(comments ?? "").trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update transaction ──
     PUT /api/InventoryTransactionDtl
     Body: full object with transactionID
  ── */
  update: ({
    transactionID,
    inventoryItemID = 0,
    stockID         = 0,
    productID,
    transactionType,
    quantity,
    comments        = "",
    lastUPDTID      = 0,
  }) =>
    request("/InventoryTransactionDtl", {
      method: "PUT",
      body: JSON.stringify({
        transactionID,
        inventoryItemID: Number(inventoryItemID) || 0,
        stockID:         Number(stockID)         || 0,
        productID:       Number(productID),
        transactionType,
        quantity:        Number(quantity),
        comments:        String(comments ?? "").trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE transaction by ID ──
     DELETE /api/InventoryTransactionDtl/{transactionID}
  ── */
  remove: (transactionID) =>
    request(`/InventoryTransactionDtl/${transactionID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   CATALOGUE ITEM API
   ─────────────────────────────────────────────
   Paste after the existing API blocks in api.js.
   Uses the shared BASE_URL + request() helper —
   change BASE_URL once to move dev → UAT → prod.
════════════════════════════════════════════ */

export const catalogueItemApi = {

  /* ── GET all catalogue items ──
     GET /api/CatalogueItem
     Returns: array of catalogue item objects
     {
       catalogueItemID, productID,
       displayName, descriptionLbl,
       isFeatured, displaySection,
       isActive, description, comments,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/CatalogueItem", { method: "GET" }),

  /* ── GET single item by ID ──
     GET /api/CatalogueItem/{catalogueItemID}
  ── */

// Inside cataloguePhotoApi, after getById:
getByItemId: (catalogueItemID) =>
  request(`/CataloguePhoto/catalogue/${catalogueItemID}`, { method: "GET" }),

  /* ── POST create catalogue item ──
     POST /api/CatalogueItem
     Body: {
       catalogueItemID: 0,           // always 0 for new
       productID:       number,      // required
       displayName:     string,      // required
       descriptionLbl:  string,      // short label
       isFeatured:      boolean,
       displaySection:  string,      // e.g. "Homepage"
       isActive:        boolean,
       description:     string,      // full description
       comments:        string,      // internal notes
       lastUPDTTM:      string,      // ISO datetime — auto-set
       lastUPDTID:      number,      // 0 default
     }
  ── */
  create: ({
    productID,
    displayName,
    descriptionLbl  = "",
    isFeatured      = false,
    displaySection  = "",
    isActive        = true,
    description     = "",
    comments        = "",
    lastUPDTID      = 0,
  }) =>
    request("/CatalogueItem", {
      method: "POST",
      body: JSON.stringify({
        catalogueItemID: 0,
        productID:       Number(productID),
        displayName:     displayName.trim(),
        descriptionLbl:  descriptionLbl.trim(),
        isFeatured,
        displaySection:  displaySection.trim(),
        isActive,
        description:     description.trim(),
        comments:        comments.trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update catalogue item ──
     PUT /api/CatalogueItem
     Body: full object with catalogueItemID
  ── */
  update: ({
    catalogueItemID,
    productID,
    displayName,
    descriptionLbl  = "",
    isFeatured      = false,
    displaySection  = "",
    isActive        = true,
    description     = "",
    comments        = "",
    lastUPDTID      = 0,
  }) =>
    request("/CatalogueItem", {
      method: "PUT",
      body: JSON.stringify({
        catalogueItemID,
        productID:       Number(productID),
        displayName:     displayName.trim(),
        descriptionLbl:  descriptionLbl.trim(),
        isFeatured,
        displaySection:  displaySection.trim(),
        isActive,
        description:     description.trim(),
        comments:        comments.trim(),
        lastUPDTTM:      new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE catalogue item by ID ──
     DELETE /api/CatalogueItem/{catalogueItemID}
  ── */
  remove: (catalogueItemID) =>
    request(`/CatalogueItem/${catalogueItemID}`, { method: "DELETE" }),
};


/* ════════════════════════════════════════════
   CATALOGUE PHOTO API
   ─────────────────────────────────────────────
   Uses multipart/form-data for upload (POST/PUT).
   NOTE: We do NOT use the shared request() helper
   here because that sets Content-Type: application/json.
   For multipart, we let the browser set the boundary
   automatically by NOT setting Content-Type at all.
════════════════════════════════════════════ */

export const cataloguePhotoApi = {

  /* ── GET all photos ──
     GET /api/CataloguePhoto
     Returns: flat array of all photo records.
     Filter client-side by catalogueItemID to get
     photos for a specific catalogue item.
     {
       photoID, catalogueItemID,
       photos,           // array of file URLs or base64
       filepath, filename,
       comments, displayOrder, isPrimary
     }
  ── */
  getAll: () =>
    request("/CataloguePhoto", { method: "GET" }),

  /* ── GET single photo by photoID ──
     GET /api/CataloguePhoto/{photoID}
  ── */
  getById: (photoID) =>
    request(`/CataloguePhoto/${photoID}`, { method: "GET" }),
  getByItemId: (catalogueItemID) =>
    request(`/CataloguePhoto/catalogue/${catalogueItemID}`, { method: "GET" }),

  /* ── POST upload a new photo ──
     POST /api/CataloguePhoto
     Content-Type: multipart/form-data (browser sets boundary)
     Fields:
       PhotoID          → "0"           (always 0 for new)
       CatalogueItemID  → string number
       Photos           → File object   (the image file)
       Filepath         → string        (can be "")
       Filename         → string        (file.name)
       Comments         → string        (caption / note)
       DisplayOrder     → string number
       IsPrimary        → "true"/"false"
  ── */
upload: ({
  catalogueItemID,
  files,
  comments     = "",
  displayOrder = 1,
  isPrimary    = false,
}) => {
  const token = localStorage.getItem("authToken");
  const fd    = new FormData();

  const fileArray = Array.from(files);

  fd.append("PhotoID",         "0");
  fd.append("CatalogueItemID", String(catalogueItemID));

  // Append ALL files in one request — backend names them image1, image2... sequentially
  fileArray.forEach(file => {
    fd.append("Photos", file, file.name);
  });

  fd.append("Filepath",     fileArray[0].name);
  fd.append("Filename",     fileArray[0].name);
  fd.append("Comments",     comments);
  fd.append("DisplayOrder", String(displayOrder));
  fd.append("IsPrimary",    String(isPrimary));

  return fetch(`${BASE_URL}/CataloguePhoto`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: fd,
  }).then(async res => {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(
        data?.message || data?.Message || data?.title ||
        `Upload failed (${res.status})`
      );
    }
    return data;
  });
},

  /* ── PUT update photo metadata ──
     PUT /api/CataloguePhoto
     Content-Type: multipart/form-data
     Same fields as POST, but PhotoID is the existing ID.
     You may omit the Photos field if not replacing the file —
     send an empty string in that case so the server keeps
     the existing file (verify with your backend team).
  ── */
update: ({
  photoID,
  catalogueItemID,
  file         = null,
  filepath     = "",
  filename     = "",
  comments     = "",
  displayOrder = 1,
  isPrimary    = false,
}) => {
  const token        = localStorage.getItem("authToken");
  const fd           = new FormData();
  const resolvedName = file ? (filename || file.name) : filename;  // ✅ keep this
  const resolvedPath = filepath || resolvedName;                    // ✅ keep this

  fd.append("PhotoID",         String(photoID));
  fd.append("CatalogueItemID", String(catalogueItemID));
  if (file) {
    fd.append("Photos",   file, resolvedName);
    fd.append("Filename", resolvedName);
  } else {
    fd.append("Filename", resolvedName);
  }
  fd.append("Filepath",     resolvedPath);
  fd.append("Comments",     comments);
  fd.append("DisplayOrder", String(displayOrder));
  fd.append("IsPrimary",    String(isPrimary));

  return fetch(`${BASE_URL}/CataloguePhoto`, {
    method: "PUT",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: fd,
  }).then(async res => {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(
        data?.message || data?.Message || data?.title ||
        `Update failed (${res.status})`
      );
    }
    return data;
  });
},

  /* ── DELETE photo by photoID ──
     DELETE /api/CataloguePhoto/{photoID}
  ── */
  remove: (photoID) =>
    request(`/CataloguePhoto/${photoID}`, { method: "DELETE" }),
};







export default apiService;